import { collection, doc, getDocs, getDoc, query, orderBy, deleteDoc, writeBatch } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";
import { mediaToFirestore } from "../models/media/mediaConverter";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";

/**
 * MediaService
 * CRUD de mídias para exibição no Totem/TVDisplay.
 * Coleção: tenants/{idTenant}/medias
 * Storage: tenants/{idTenant}/medias/{mediaId}/image
 */
const MediaService = {
    /**
     * Busca todas as mídias do tenant.
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.MEDIAS);
        const q = query(colRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    },

    /**
     * Busca uma mídia pelo ID.
     */
    getById: async (idTenant, mediaId) => {
        if (!idTenant || !mediaId) throw new Error("Parâmetros inválidos.");

        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEDIAS, mediaId);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) return null;
        return { id: snapshot.id, ...snapshot.data() };
    },

    /**
     * Busca mídias ativas dentro do período atual.
     */
    getActiveForToday: async (idTenant, todayStr) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.MEDIAS);
        const q = query(colRef, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);

        return snapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(m => {
                if (m.status !== "active") return false;
                if (m.startDate && m.startDate > todayStr) return false;
                if (m.endDate && m.endDate < todayStr) return false;
                return true;
            });
    },

    /**
     * Faz upload da imagem da mídia para o Firebase Storage.
     * Aceita File object (input type="file").
     * 
     * @param {string} idTenant
     * @param {string} mediaId - ID do documento (pode ser gerado antes)
     * @param {File}   file    - Arquivo de imagem
     * @returns {Promise<string>} URL de download
     */
    uploadImage: async (idTenant, mediaId, file) => {
        if (!file) throw new Error("Arquivo é obrigatório.");

        const ext = file.name.split('.').pop() || "jpg";
        const storagePath = `tenants/${idTenant}/medias/${mediaId}/image.${ext}`;
        const fileRef = ref(storage, storagePath);

        await uploadBytes(fileRef, file, { contentType: file.type });
        return getDownloadURL(fileRef);
    },

    /**
     * Faz upload de múltiplas imagens do carrossel para o Firebase Storage.
     * @param {string} idTenant
     * @param {string} mediaId
     * @param {File[]} files - Array de arquivos de imagem
     * @returns {Promise<Array<{url: string, order: number}>>}
     */
    uploadCarouselImages: async (idTenant, mediaId, files) => {
        if (!files || files.length === 0) return [];

        const results = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const ext = file.name.split('.').pop() || "jpg";
            const storagePath = `tenants/${idTenant}/medias/${mediaId}/carousel_${i}.${ext}`;
            const fileRef = ref(storage, storagePath);

            await uploadBytes(fileRef, file, { contentType: file.type });
            const url = await getDownloadURL(fileRef);
            results.push({ url, order: i });
        }
        return results;
    },

    /**
     * Salva ou atualiza uma mídia.
     * Se tiver imageFile (File), faz upload primeiro.
     * Se tiver carouselFiles (File[]), faz upload de todas.
     */
    save: async (formData, idTenant, authUser, imageFile = null, carouselFiles = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const colRef = collection(db, "tenants", idTenant, COLLECTIONS.MEDIAS);
        const batch = writeBatch(db);
        let docRef;

        if (formData.id) {
            docRef = doc(colRef, formData.id);
        } else {
            docRef = doc(colRef);
        }

        // Upload de imagem única (type === "single")
        let imageUrl = formData.imageUrl;
        if (imageFile) {
            imageUrl = await MediaService.uploadImage(idTenant, docRef.id, imageFile);
        }

        // Upload de imagens do carrossel (type === "carousel")
        let carouselImages = formData.carouselImages || [];
        if (carouselFiles && carouselFiles.length > 0) {
            const newUploads = await MediaService.uploadCarouselImages(idTenant, docRef.id, carouselFiles);
            // Mesclar com imagens existentes que não foram substituídas
            const existingKept = carouselImages.filter(img => typeof img.url === "string" && img.url.startsWith("http"));
            carouselImages = [...existingKept, ...newUploads]
                .map((img, idx) => ({ ...img, order: idx }));
        }

        const payload = mediaToFirestore(
            { ...formData, imageUrl, carouselImages },
            { id: idTenant },
            authUser
        );

        batch.set(docRef, payload, { merge: true });
        await batch.commit();

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.SETTINGS,
            action: formData.id ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
            entityId: docRef.id,
            entityName: payload.name || "Mídia",
            description: `${formData.id ? 'Atualizou' : 'Criou'} mídia "${payload.name}" (${payload.type === "carousel" ? "carrossel" : "imagem única"})`,
        });

        return { id: docRef.id, ...payload, imageUrl, carouselImages };
    },

    /**
     * Remove uma mídia e sua imagem do Storage.
     */
    delete: async (mediaId, idTenant, authUser, mediaName = "") => {
        if (!idTenant || !mediaId) throw new Error("Parâmetros obrigatórios ausentes.");

        // Tentar remover imagem do Storage (silenciar erro se não existir)
        try {
            // Tentar deletar formatos comuns de imagem
            for (const ext of ["jpg", "jpeg", "png", "webp"]) {
                try {
                    await deleteObject(ref(storage, `tenants/${idTenant}/medias/${mediaId}/image.${ext}`));
                } catch { /* silenciar */ }
            }
        } catch { /* silenciar */ }

        // Remover documento
        const docRef = doc(db, "tenants", idTenant, COLLECTIONS.MEDIAS, mediaId);
        await deleteDoc(docRef);

        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.SETTINGS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: mediaId,
            entityName: mediaName || "Mídia",
            description: `Excluiu mídia "${mediaName || mediaId}"`,
        });
    }
};

export default MediaService;
